<template>
  <div class="pl-4 py-2 pr-6 grow flex justify-between items-center gap-1">
    <ViewSelector
      :views="views"
      :type="type"
      :currentView="currentView"
      :allowViewCreation="false"
      v-show="!includes(activeViewControls, 'search')"
      @selectView="handleSelectView"
      @deleteView="handleDeleteView"
      @addView="handleAddView"
      @renameView="handleRenameView"
    />
    <ViewSearch
      v-if="includes(activeViewControls, 'search')"
      :query="query"
      :placeholder="
        t('components.inputs.search_in_view.placeholder', {
          view: currentView.name,
        })
      "
      @search="handleSearchChange($event?.target?.value || $event)"
      @close="handleCloseSearch"
    />
    <ViewControls
      :type="type"
      :viewType="viewType"
      :activeViewControls="activeViewControls"
      @toggle="handleViewControls"
    />
  </div>
  <div class="pl-4 pr-6 pb-2" v-if="includes(activeViewControls, 'filter')">
    <ViewFilters
      ref="viewFilters"
      :type="type"
      :views="views"
      :currentView="currentView"
      @addView="handleAddView"
      @updateView="handleUpdateView"
      @closeViewFilters="removeViewControl('filter')"
    />
  </div>
</template>

<script setup>
// Import Vue Utilities
import { ref, onUnmounted } from "vue";

// Import Utilities
import { includes, without } from "lodash";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

// Composable imports
import { useSearch } from "@/Composables/useSearch";
import { useFilters } from "@/Composables/useFilters";

// Import Components
import {
  ViewControls,
  ViewFilters,
  ViewSearch,
  ViewSelector,
} from "@/SharedComponents/PageHeader/Partials";

// Emits
const emit = defineEmits([
  "selectView",
  "deleteView",
  "addView",
  "renameView",
  "updateView",
]);

// Define Props
const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  viewType: {
    type: String,
    required: true,
  },
  views: {
    type: Array,
    required: true,
  },
  currentView: {
    type: Object,
    required: true,
  },
});

// Search
const searchStore = useSearch(props.type);
const { query } = storeToRefs(searchStore);
const { handleSearchChange, handleClearSearch } = searchStore;

// Filters
const filterStore = useFilters(props.type);
const { handleClearFilters } = filterStore;

// Translations
const { t } = useI18n();

// Refs
const viewFilters = ref(null);
const activeViewControls = ref([]);

// Functions
const handleSelectView = (view) => {
  emit("selectView", view);
  viewFilters.value?.handleApplyViewFilters();
};

const handleDeleteView = (viewId) => {
  emit("deleteView", viewId);
};

const handleAddView = (view) => {
  emit("addView", view);
};

const handleRenameView = (view) => {
  emit("renameView", view);
};

const handleUpdateView = (view) => {
  emit("updateView", view);
};

const handleViewControls = (control) => {
  includes(activeViewControls.value, control)
    ? removeViewControl(control)
    : addViewControl(control);
};

const removeViewControl = (control) => {
  activeViewControls.value = without(activeViewControls.value, control);
};

const addViewControl = (control) => {
  activeViewControls.value = [...activeViewControls.value, control];
};

function handleCloseSearch() {
  removeViewControl("search");
  handleClearSearch();
}

// Lifecycle Hooks
onUnmounted(() => {
  handleClearSearch();
});

// Exposed functions
defineExpose({
  reset: () => {
    handleCloseSearch();

    if (props.type !== "asset_groups") {
      handleClearFilters();
      viewFilters.value?.handleApplyViewFilters();
    }
  },
});
</script>
